<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { UseStoreAdmin } from '@/store/adminStore';
import { computed, nextTick, ref } from 'vue';
import { UseStoreUser } from '@/store/userStore';
import { ACCEPTED_IMAGE_TYPES, LINK_TEXT_PLACEHOLDER } from '@/constants/liens';

const isAddButtonDisabled = ref<boolean | null>(false);
const acceptedTypes = ref(ACCEPTED_IMAGE_TYPES);
const { t } = useI18n();
const linkToDelete = ref<string | null>(null);
const isDeleteClick = ref(false);
const adminStore = UseStoreAdmin();
const userStore = UseStoreUser();
const newEntry = ref<{
  name: string;
  url: string;
  file: File | null;
}>({
  name: '',
  url: '',
  file: null,
});
const isNew = ref(false);
const editableLink = ref<string | null>(null);
const targetSection = ref<HTMLElement | null>(null);
const editableValue = ref<any>();
const uploadEdit = ref<boolean>(false);
const isCreateDisabled = computed(() => shouldDisable(false));
const isEditDisabled = computed(() => {
  return !isValidEntry(editableValue.value, true);
});
const removeFile = () => {
  editableValue.value = {
    ...editableValue.value,
    file: null,
    imageBase64: '',
    s3Key: '',
  };
  uploadEdit.value = true;
};

function createNewEntry() {
  isAddButtonDisabled.value = true;
  editableLink.value = null;
  isNew.value = true;
  nextTick(() => {
    targetSection.value?.scrollIntoView({ behavior: 'smooth' });
  });
}
function updateValue(event: string, field: string) {
  editableValue.value = {
    ...editableValue.value,
    [field]: event,
  };
}
function createValue(event: string, field: string) {
  newEntry.value = {
    ...newEntry.value,
    [field]: event,
  };
}
function changeFile(file: any, isupdate: boolean = false) {
  if (
    file &&
    (file.type.startsWith('image/') || file.type == 'application/octet-stream')
  ) {
    if (isupdate) {
      editableValue.value = {
        ...editableValue.value,
        file: file,
      };
    } else {
      newEntry.value = {
        ...newEntry.value,
        file: file,
      };
    }
  }
}
async function saveAdmin() {
  isAddButtonDisabled.value = false;
  const userId = userStore.user.id;
  if (!userId) return;

  await adminStore.createAdminLink({
    name: newEntry.value.name,
    url: newEntry.value.url,
    userId,
    ...(newEntry.value.file ? { file: newEntry.value.file } : {}),
  });

  newEntry.value = {
    name: '',
    url: '',
    file: null,
  };

  isNew.value = false;
}

function cancel(isUpdate: boolean = false) {
  isAddButtonDisabled.value = false;
  if (isUpdate) {
    editableLink.value = null;
    editableValue.value = null;
    uploadEdit.value = false;
  } else {
    newEntry.value = {
      name: '',
      url: '',
      file: null,
    };
    isNew.value = false;
  }
}
function base64ToFile(base64: string, filename: string): File {
  if (base64.startsWith('data:application/octet-stream')) {
    if (base64.includes('svg')) {
      base64 = base64.replace('application/octet-stream', 'image/svg+xml');
    } else {
      base64 = base64.replace('application/octet-stream', 'image/png');
    }
  }

  const arr = base64.split(',');
  const mimeMatch = arr[0].match(/:(.*?);/);

  if (!mimeMatch) {
    throw new Error('Invalid base64 format');
  }

  const mime = mimeMatch[1];
  const byteString = atob(arr[1]);
  const u8arr = new Uint8Array(byteString.length);

  for (let i = 0; i < byteString.length; i++) {
    u8arr[i] = byteString.charCodeAt(i);
  }

  return new File([u8arr], filename, { type: mime });
}
function editLink(link: any) {
  isAddButtonDisabled.value = true;
  isNew.value = false;
  editableLink.value = link.id;
  editableValue.value = { ...link };
  uploadEdit.value = false;
  setTimeout(() => {
    const file = base64ToFile(
      editableValue.value.imageBase64,
      editableValue.value.s3Key
    );
    if (file) {
      editableValue.value = {
        ...editableValue.value,
        file: file,
      };
    }
  }, 1000);
}

function isValidUrl(url: string): boolean {
  try {
    const parsed = new URL(url);
    return ['http:', 'https:'].includes(parsed.protocol);
  } catch {
    return false;
  }
}

async function update() {
  isAddButtonDisabled.value = false;
  await adminStore.updateLinkByAdmin({
    ...editableValue.value,
    userId: userStore.user.id,
  });
  editableLink.value = null;
  editableValue.value = null;
}

function isValidEntry(
  entry: { name?: string; url?: string; file?: File },
  requireFile: boolean
): any {
  return (
    entry.name?.trim() &&
    entry.url?.trim() &&
    isValidUrl(entry.url) &&
    (!requireFile || (entry.file && entry.file.size > 0))
  );
}
function shouldDisable(isUpdate: boolean): boolean {
  return !isValidEntry(
    isUpdate ? editableValue.value : newEntry.value,
    !isUpdate
  );
}

function confirmDelete(linkId: string): void {
  adminStore.deleteAdminLink(linkId);
  isDeleteClick.value = false;
  linkToDelete.value = null;
}

function getCancelActions(_linkId: string) {
  return [
    {
      text: t('panel.Cancel'),
      callback: () => {
        linkToDelete.value = null;
      },
    },
  ];
}

function getMainActions(linkId: string) {
  return [
    {
      text: t('panel.Delete'),
      status: 'destructive',
      callback: () => {
        confirmDelete(linkId);
      },
    },
  ];
}
</script>

<template>
  <cs-message>
    {{ t('panel.csMessageAdmin') }}
  </cs-message>
  <div class="header">
    <h2>{{ t('panel.titleLienUtilAdmin') }}</h2>
    <cs-button
      class="btn-new-link"
      icon="ajouter"
      type="secondary"
      :text="t('modal.nouveauLien')"
      @click="createNewEntry"
      data-qa="add-link"
      :disabled="isAddButtonDisabled"
    />
  </div>
  <ul class="link-list" data-qa="Liste-liens-admin">
    <li
      v-for="link in adminStore.adminLinks"
      :key="link.id"
      :class="['link-item', { 'edit-link': editableLink === link.id }]"
    >
      <div class="link-text">
        <strong class="link-title">{{ link.name }}</strong>
        <div v-if="editableLink !== link.id" class="link-subtitle">
          {{ link.url }}
        </div>
      </div>
      <div class="actions">
        <cs-button
          :aria-label="t('links.editLink', { name: link.name })"
          :title="t('links.editLink', { name: link.name })"
          icon="modifier"
          type="tertiary"
          size="tiny"
          data-cy-id="button-success-7"
          @click="editLink(link)"
        />
        <cs-button
          :aria-label="t('links.deleteLink', { name: link.name })"
          :title="t('links.deleteLink', { name: link.name })"
          type="secondary"
          icon="supprimer"
          size="small"
          status="destructive"
          @click="linkToDelete = link.id"
        />
        <cs-modal
          v-if="linkToDelete === link.id"
          class="confirmation-popup-delete"
          data-qa="confirmation-popup-delete"
          :is-opened="linkToDelete === link.id"
          :title="t('modal.titleDeleteModal')"
          :cancel-actions="getCancelActions(link.id)"
          :main-actions="getMainActions(link.id)"
        >
          <template #default>
            <span
              class="Confirm-text"
              v-html="t('modal.confirmDeleteLienByAdmin', { url: link.url })"
            />
          </template>
        </cs-modal>
      </div>
      <div v-if="editableLink == link.id" class="newEntry">
        <label for="nameLink">{{ t('modal.labelName') }} </label>
        <CsInputText
          id="nameLink"
          category="primary"
          :disabled="false"
          minLength="0"
          maxLength="50"
          size="regular"
          :placeholder="'Ex: Cegedim Business Services'"
          :modelValue="editableValue.name"
          @update:modelValue="updateValue($event, 'name')"
        />
        <label for="link">{{ t('modal.labelLien') }}</label>
        <CsInputText
          id="link"
          category="primary"
          :disabled="false"
          :minLength="0"
          size="regular"
          :placeholder="LINK_TEXT_PLACEHOLDER"
          :modelValue="editableValue.url"
          @update:modelValue="updateValue($event, 'url')"
        />
        <label for="file">{{ t('modal.labelImage') }}</label>
        <cs-file-card
          v-if="!uploadEdit"
          :file-name="editableValue.s3Key"
          format="line"
        >
          <template #default>
            <cs-button
              icon="effacer"
              type="tertiary"
              status="default"
              @click="removeFile"
            />
          </template>
        </cs-file-card>
        <cs-upload
          v-if="uploadEdit"
          show-file-name
          :accept="acceptedTypes"
          :allowedTypes="acceptedTypes"
          v-model="editableValue.file"
          @change="changeFile($event, true)"
        />
        <div class="footer">
          <cs-button
            type="secondary"
            :text="t('panel.Cancel')"
            @click="cancel(true)"
            data-qa="cancel-button"
          />
          <cs-button
            icon="enregistrer"
            type="primary"
            :text="t('modal.saveUpdate')"
            @click="update"
            data-qa="save-button"
            :disabled="isEditDisabled"
          />
        </div>
      </div>
    </li>
  </ul>
  <div v-if="isNew" ref="targetSection" class="newEntry">
    <h3>{{ t('modal.nouveauLien') }}</h3>
    <label for="nameLink">{{ t('modal.labelName') }}</label>
    <CsInputText
      id="nameLink"
      category="primary"
      :disabled="false"
      minLength="0"
      maxLength="50"
      size="regular"
      :placeholder="'Ex :Cegedim Business Services'"
      v-modelValue="newEntry.name"
      @update:modelValue="createValue($event, 'name')"
    />
    <label for="link">{{ t('modal.labelLien') }}</label>
    <CsInputText
      id="link"
      category="primary"
      :disabled="false"
      :minLength="0"
      size="regular"
      :placeholder="LINK_TEXT_PLACEHOLDER"
      v-modelValue="newEntry.url"
      @update:modelValue="createValue($event, 'url')"
    />
    <label for="file">{{ t('modal.labelImage') }}</label>
    <cs-file-drop-zone
      id="file"
      :acceptedTypes="acceptedTypes"
      @change="changeFile"
    />
    <div class="footer">
      <cs-button
        type="secondary"
        :text="t('panel.Cancel')"
        @click="cancel(false)"
        data-qa="cancel-button"
      />
      <cs-button
        icon="enregistrer"
        type="primary"
        :text="t('panel.Save')"
        @click="saveAdmin"
        data-qa="save-button"
        :disabled="isCreateDisabled"
      />
    </div>
  </div>
</template>

<style scoped>
.btn-new-link {
  background-color: #f0f8ff;
  border: 1px solid #3399ff;
  color: #3399ff;
  padding: 0.3rem 0.75rem;
  border-radius: 5px;
  font-size: 0.875rem;
  cursor: pointer;
}

.link-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.link-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0;
  border-bottom: 1px solid #f0f0f0;
  flex-wrap: wrap;
}

.link-text {
  max-width: 75%;
}

.actions {
  display: flex;
  gap: 0.5rem;
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.btn-icon {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;
}

.btn-icon.danger {
  color: red;
}
.btn-close {
  padding: 0.5rem 1rem;
  border-radius: 5px;
  background-color: #f1f5f9;
  border: none;
  cursor: pointer;
}
.link-title {
  font-size: 0.813rem;
}
.link-subtitle {
  font-size: 0.75rem;
}
.newEntry {
  display: flex;
  flex-direction: column;
  width: 95%;
  background-color: rgba(250, 250, 250, 1);
  padding: 0rem 0.5rem 0.5rem 0.5rem;
  border-radius: 8px;
  margin-top: 0.5rem;
}
:deep(.cs-input-text .cs-input-text-input.regular) {
  width: 26rem;
}
:deep(.cs-upload) {
  background: white;
}
:deep(.cs-input-text .cs-input-text-clear.has-max-length.regular) {
  left: 80% !important;
}
.footer {
  display: flex;
  justify-content: space-between;
  padding: 10px 0px;
}
:deep(.cs-file-card.cs-file-card-line) {
  width: 100% !important;
}
:deep(
    .cs-file-card
      .cs-file-card-container
      .cs-file-card-info
      .cs-file-card-left
      .cs-file-card-text
  ) {
  gap: 6rem;
}
</style>
